import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AppContext";
import { Loading } from "../../components/PageHeader";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { AdminTitle, Pill, ErrorBox } from "./AdminLayout";

const STATUS_TONE = { active: "green", pending: "gold", suspended: "red" };
const btn = "px-2.5 py-1 text-[0.65rem] uppercase tracking-[0.15em] border wb-line hover:border-[color:var(--wb-gold)] hover:text-wb-gold disabled:opacity-30 disabled:pointer-events-none transition-colors";

function ApprovalToggle() {
  const [on, setOn] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setOn(r.data.require_approval)).catch((e) => toast.error(errMsg(e))); }, []);
  const toggle = async (v) => {
    try { await api.put("/admin/settings", { require_approval: v }); setOn(v); toast.success(v ? "New accounts now require approval" : "New accounts are active immediately"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <label className="wb-card flex items-center gap-4 p-4 text-sm" data-testid="approval-setting">
      <Switch checked={!!on} disabled={on === null} onCheckedChange={toggle} data-testid="require-approval-switch" />
      Require approval for new accounts
    </label>
  );
}

function RoleSelect({ roles, value, onChange, disabled, testid }) {
  const options = useMemo(() => roles.filter((r) => r.key !== "super_admin" || value === "super_admin"), [roles, value]);
  return (
    <select className="wb-input" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {options.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
    </select>
  );
}

function UserDialog({ u, roles, onClose, onSaved }) {
  const isNew = !u;
  const [f, setF] = useState(u ? { name: u.name, email: u.email, role: u.role, status: u.status } : { name: "", email: "", password: "", role: "customer", status: "active" });
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  const save = async () => {
    setBusy(true);
    try {
      const { data } = isNew ? await api.post("/admin/users", f) : await api.patch(`/admin/users/${u.id}`, f);
      onSaved(data, isNew); toast.success(isNew ? "User created" : "User updated"); onClose();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const reset = async () => {
    try { await api.post(`/admin/users/${u.id}/reset-password`, { password: pw }); toast.success("Password reset — existing sessions signed out"); setPw(""); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const lock = u?.is_owner;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none" data-testid="user-dialog">
        <DialogTitle className="font-display text-3xl font-normal">{isNew ? "New user" : "Edit user"}</DialogTitle>
        <div className="space-y-4">
          <div><label className="wb-label">Name</label><input className="wb-input" value={f.name} onChange={set("name")} data-testid="user-form-name" /></div>
          <div><label className="wb-label">Email</label><input type="email" className="wb-input" value={f.email} disabled={lock} onChange={set("email")} data-testid="user-form-email" /></div>
          {isNew && <div><label className="wb-label">Password (min 8)</label><input type="password" className="wb-input" value={f.password} onChange={set("password")} data-testid="user-form-password" /></div>}
          <div className="grid grid-cols-2 gap-4">
            <div><label className="wb-label">Role</label><RoleSelect roles={roles} value={f.role} disabled={lock} onChange={set("role")} testid="user-form-role" /></div>
            <div><label className="wb-label">Status</label>
              <select className="wb-input" value={f.status} disabled={lock} onChange={set("status")} data-testid="user-form-status">
                <option value="active">Active</option><option value="pending">Pending</option><option value="suspended">Suspended</option>
              </select>
            </div>
          </div>
          <button onClick={save} disabled={busy} className="btn-gold w-full" data-testid="user-form-save">{busy ? "Saving…" : isNew ? "Create user" : "Save changes"}</button>
          {!isNew && (
            <div className="border-t wb-line pt-4">
              <label className="wb-label">Set new password (min 8)</label>
              <div className="flex gap-2">
                <input type="password" className="wb-input" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" />
                <button onClick={reset} disabled={pw.length < 8} className="btn-ghost shrink-0" data-testid="reset-password-button">Reset</button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeleteUser({ u, disabled, onDeleted }) {
  const del = async () => {
    try { const { data } = await api.delete(`/admin/users/${u.id}`); toast.success(`User deleted (${data.orders_kept} order(s) kept)`); onDeleted(u.id); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger className={btn} disabled={disabled} data-testid="user-delete-button">Delete</AlertDialogTrigger>
      <AlertDialogContent className="rounded-none">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-2xl">Delete {u.email}?</AlertDialogTitle>
          <AlertDialogDescription>The account is removed permanently. Their orders are kept and marked as “Deleted user”.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none" data-testid="confirm-user-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction className="rounded-none bg-red-600 text-white hover:bg-red-700" onClick={del} data-testid="confirm-user-delete-button">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function UserRow({ u, me, onSaved, onEdit, onDeleted }) {
  const patch = async (body, msg) => {
    try { onSaved((await api.patch(`/admin/users/${u.id}`, body)).data); toast.success(msg); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const locked = u.is_owner || u.id === me.id;
  return (
    <tr className="border-b wb-line hover:bg-wb-card" data-testid={`user-row-${u.email}`}>
      <td className="px-4 py-3">{u.name || "—"} {u.is_owner && <Pill tone="gold">Owner</Pill>}</td>
      <td className="px-4 py-3 muted">{u.email}</td>
      <td className="px-4 py-3"><Pill tone={u.permissions?.length ? "gold" : "muted"} testid="user-role">{u.role_name}</Pill></td>
      <td className="px-4 py-3"><Pill tone={STATUS_TONE[u.status]} testid="user-status">{u.status}</Pill></td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap justify-end gap-1.5">
          {u.status === "pending" && <button className={btn} onClick={() => patch({ status: "active" }, "User approved")} data-testid="user-approve-button">Approve</button>}
          {u.status === "suspended"
            ? <button className={btn} onClick={() => patch({ status: "active" }, "User reactivated")} data-testid="user-reactivate-button">Reactivate</button>
            : <button className={btn} disabled={locked} onClick={() => patch({ status: "suspended" }, "User suspended")} data-testid="user-suspend-button">Suspend</button>}
          <button className={btn} onClick={() => onEdit(u)} data-testid="user-edit-button">Edit</button>
          <DeleteUser u={u} disabled={locked} onDeleted={onDeleted} />
        </div>
      </td>
    </tr>
  );
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const role = params.get("role") || "";
  const [q, setQ] = useState("");
  const [users, setUsers] = useState(null);
  const [roles, setRoles] = useState([]);
  const [err, setErr] = useState("");
  const [dialog, setDialog] = useState(null);
  useEffect(() => { api.get("/admin/roles").then((r) => setRoles(r.data.roles)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/users", { params: { q: q || undefined, status: status || undefined, role: role || undefined } })
      .then((r) => { setUsers(r.data); setErr(""); }).catch((e) => setErr(errMsg(e))), 250);
    return () => clearTimeout(t);
  }, [q, status, role]);
  const setParam = (k, v) => { const n = new URLSearchParams(params); v ? n.set(k, v) : n.delete(k); setParams(n); };
  const onSaved = (u, isNew) => setUsers((prev) => (isNew ? [u, ...prev] : prev.map((x) => (x.id === u.id ? u : x))));
  const onDeleted = (id) => setUsers((prev) => prev.filter((x) => x.id !== id));
  return (
    <div data-testid="admin-users">
      <AdminTitle eyebrow="Community" title="Users">
        <ApprovalToggle />
        <button className="btn-gold" onClick={() => setDialog("new")} data-testid="user-create-button"><Plus size={14} /> New user</button>
      </AdminTitle>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <input className="wb-input" placeholder="Search name or email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="users-search-input" />
        <select className="wb-input sm:!w-48" value={role} onChange={(e) => setParam("role", e.target.value)} data-testid="users-role-filter">
          <option value="">All roles</option>{roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
        </select>
        <select className="wb-input sm:!w-48" value={status} onChange={(e) => setParam("status", e.target.value)} data-testid="users-status-filter">
          <option value="">All statuses</option><option value="active">Active</option><option value="pending">Pending</option><option value="suspended">Suspended</option>
        </select>
      </div>
      {err ? <ErrorBox msg={err} /> : !users ? <Loading /> : users.length === 0 ? <p className="py-10 muted" data-testid="users-empty">No users match.</p> : (
        <div className="overflow-x-auto border wb-line">
          <table className="w-full text-sm">
            <thead><tr className="border-b wb-line text-left">{["Name", "Email", "Role", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-mono text-[0.62rem] uppercase tracking-[0.2em] font-normal muted">{h}</th>)}</tr></thead>
            <tbody>{users.map((u) => <UserRow key={u.id} u={u} me={me} onSaved={onSaved} onEdit={setDialog} onDeleted={onDeleted} />)}</tbody>
          </table>
        </div>
      )}
      {dialog && <UserDialog u={dialog === "new" ? null : dialog} roles={roles} onClose={() => setDialog(null)} onSaved={onSaved} />}
    </div>
  );
}
